/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Host, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  CELL_TEXT_PRIMARY_CLASS,
  code3Formatter,
  falsyCellRenderer,
  formatCostCenterFinderOptionLabel,
  rendererIconByName,
} from '@gh/common-internal-shared';
import {
  getDialogInitData,
  getDialogInput,
  MutableSelectionModel,
  NumberTypes,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
  GridRowEvent,
} from '@gh/core-ui-grid';
import { RowSelectedEvent } from 'ag-grid-community';
import { getPbCcFinderListItemId, getPbComponentItemFinderListItemId } from '../../';
import { PbComponentItemFinderDialogConfig } from '../../resources/pb-component-item-finder-business.service';
import {
  PbComponentItemFinderCcListItem,
  PbComponentItemFinderListItem,
} from '../../resources/pb-component-item-finder.model';
import { AbstractPbComponentItemFinderViewComponent } from './abstract-pb-component-item-finder-view-component';
import { PbComponentItemFinderDialogComponent } from './pb-component-item-finder-dialog.component';
import { ColumnWidthPersistenceService } from '@gh/common-internal-shared';

@Component({
  standalone: false,
  selector: 'gh-pb-component-item-finder-cc-view',
  templateUrl: 'pb-component-item-finder-cc-view.component.html',
  styleUrls: ['pb-component-item-finder-cc-view.component.scss'],
})
export class PbComponentItemFinderCcViewComponent extends AbstractPbComponentItemFinderViewComponent implements OnInit {
  readonly costCenterGridFinderDisplayWith = formatCostCenterFinderOptionLabel;

  input: PbComponentItemFinderDialogConfig;
  selectionModel: MutableSelectionModel<PbComponentItemFinderListItem>;
  ccGridDefinition: GridDefinition;
  structureGridDefinition: GridDefinition;

  costCenters: PbComponentItemFinderCcListItem[];
  currentStructures: PbComponentItemFinderListItem[];
  selectedCostCenterId?: ID;

  private readonly CC_STORAGE_KEY = 'pbComponentItemFinder:ccColumnWidths';
  private readonly STRUCTURE_STORAGE_KEY = 'pbComponentItemFinder:structureColumnWidths';

  constructor(private gridDefinitionFactory: GridDefinitionFactory,
              private translationService: TranslationService,
              @Host() private finderDialog: PbComponentItemFinderDialogComponent,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private columnWidthService: ColumnWidthPersistenceService) {
    super();
  }

  ngOnInit(): void {
    const { hierarchy } = getDialogInitData(this.dialogData);

    this.input = getDialogInput(this.dialogData);
    this.selectionModel = this.finderDialog.selectionModel;

    this.costCenters = hierarchy;

    this.ccGridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'code',
          width: this.columnWidthService.getColumnWidth(this.CC_STORAGE_KEY, 'code', 115),
          headerName: this.translationService.get('lbl_number'),
          valueFormatter: code3Formatter,
          cellClass: CELL_TEXT_PRIMARY_CLASS,
        },
        { field: 'name', width: this.columnWidthService.getColumnWidth(this.CC_STORAGE_KEY, 'name', 180), headerName: this.translationService.get('lbl_description') },
      ],
      {
        rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
        getRowId: (params) => String(getPbCcFinderListItemId(params.data)),
        onColumnResized: this.columnWidthService.createColumnResizedHandler(this.CC_STORAGE_KEY),
      });

    this.structureGridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'code',
          width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'code', 115),
          headerName: this.translationService.get('lbl_code'),
          cellClass: CELL_TEXT_PRIMARY_CLASS,
          suppressSizeToFit: true,
        },
        { field: 'name', width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'name', 75), headerName: this.translationService.get('lbl_name') },
        {
          field: 'isActive',
          width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'isActive', 85),
          headerName: this.translationService.get('lbl_deleted'),
          cellRenderer: falsyCellRenderer(rendererIconByName('done')),
          suppressSizeToFit: true,
        },
        {
          field: 'uomCode',
          width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'uomCode', 68),
          headerName: this.translationService.get('lbl_uom_abbreviation'),
          suppressSizeToFit: true,
        },
        {
          field: 'price',
          width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'price', 100),
          headerName: this.translationService.get('lbl_price'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Price),
        },
        {
          field: 'origQuantity',
          width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'origQuantity', 110),
          headerName: this.translationService.get('lbl_orig_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
        {
          field: 'origQuantityComponent',
          width: this.columnWidthService.getColumnWidth(this.STRUCTURE_STORAGE_KEY, 'origQuantityComponent', 130),
          headerName: this.translationService.get('lbl_cmp_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
      ],
      {
        getRowId: (params) => String(getPbComponentItemFinderListItemId(params.data)),
        onColumnResized: this.columnWidthService.createColumnResizedHandler(this.STRUCTURE_STORAGE_KEY),
      });

    this.selectCostCenter(this.costCenters[0]);
  }

  onRowSelected(event: RowSelectedEvent): void {
    this.selectCostCenter(event.data);
  }

  onRowClicked(item: GridRowEvent<PbComponentItemFinderListItem>): void {
    if (this.isNumericSelector) {
      if (this.getCount(item.data) === 0) this.increaseCount(item.data);
      return;
    }
    super.onRowClicked(item);
  }

  private selectCostCenter(data?: PbComponentItemFinderCcListItem): void {
    if (data) {
      const { costCenterId } = data;
      this.selectedCostCenterId = costCenterId;
      this.currentStructures = data.items;
    } else {
      this.selectedCostCenterId = void 0;
    }
  }
}
